import React, { useState, useEffect, useCallback } from 'react';
import { auditLogsApi } from '../../../api/auditLogsApi';
import PageHeader from '../components/PageHeader';
import './AuditLogs.css';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 20;

  // Filters
  const [filters, setFilters] = useState({
    action: '',
    entityType: '',
    userEmail: '',
    module: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await auditLogsApi.getAuditLogs({
        page,
        size: pageSize,
        ...filters
      });

      console.log('Audit logs response:', response);

      if (response.success && response.data) {
        setLogs(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      } else if (response.data) {
        setLogs(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const fetchStats = async () => {
    try {
      const response = await auditLogsApi.getDashboardStats();
      console.log('Stats response:', response);
      if (response.success && response.data) {
        setStats(response.data);
      } else if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await auditLogsApi.getFilterOptions();
      console.log('Filter options response:', response);
      if (response.success && response.data) {
        setFilterOptions(response.data);
      } else if (response.data) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      action: '',
      entityType: '',
      userEmail: '',
      module: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setSearchInput('');
    setPage(0);
  };

  const viewLogDetails = async (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getActionBadgeClass = (action) => {
    const actionClasses = {
      'CREATE': 'badge-success',
      'UPDATE': 'badge-info',
      'DELETE': 'badge-danger',
      'LOGIN': 'badge-primary',
      'LOGOUT': 'badge-secondary',
      'LOGIN_FAILED': 'badge-warning',
      'PASSWORD_CHANGE': 'badge-info',
      'PASSWORD_RESET': 'badge-warning',
      'ROLE_CHANGE': 'badge-purple',
      'PERMISSION_CHANGE': 'badge-purple',
      'EXPORT': 'badge-teal',
      'IMPORT': 'badge-teal',
      'BULK_UPDATE': 'badge-info',
      'BULK_DELETE': 'badge-danger',
      'APPROVE': 'badge-success',
      'REJECT': 'badge-danger',
      'ACTIVATE': 'badge-success',
      'DEACTIVATE': 'badge-warning',
      'ARCHIVE': 'badge-secondary'
    };
    return actionClasses[action] || 'badge-secondary';
  };

  const formatAction = (action) => {
    if (!action) return '-';
    return action.replace(/_/g, ' ');
  };

  return (
    <div className="audit-logs-page">
      <PageHeader
        title="Audit Logs"
        subtitle="SOC2 Compliance - Track all system activities"
      />

      {/* Statistics Cards */}
      <div className="audit-stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <span>📋</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.totalLogs?.toLocaleString() || 0}</span>
            <span className="stat-label">Total Logs</span>
          </div>
        </div>
        <div className="stat-card today">
          <div className="stat-icon">
            <span>🕐</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.logsLast24Hours?.toLocaleString() || 0}</span>
            <span className="stat-label">Last 24 Hours</span>
          </div>
        </div>
        <div className="stat-card logins">
          <div className="stat-icon">
            <span>🔑</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.loginCount || 0}</span>
            <span className="stat-label">Logins (7 days)</span>
          </div>
        </div>
        <div className="stat-card failed">
          <div className="stat-icon">
            <span>⚠️</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.loginFailedCount || 0}</span>
            <span className="stat-label">Failed Logins</span>
          </div>
        </div>
        <div className="stat-card creates">
          <div className="stat-icon">
            <span>➕</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.createCount || 0}</span>
            <span className="stat-label">Creates (7 days)</span>
          </div>
        </div>
        <div className="stat-card updates">
          <div className="stat-icon">
            <span>✏️</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.updateCount || 0}</span>
            <span className="stat-label">Updates (7 days)</span>
          </div>
        </div>
        <div className="stat-card deletes">
          <div className="stat-icon">
            <span>🗑️</span>
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats?.deleteCount || 0}</span>
            <span className="stat-label">Deletes (7 days)</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="audit-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search logs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Action</label>
            <select
              name="action"
              value={filters.action}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Actions</option>
              {filterOptions?.actions?.map(action => (
                <option key={action} value={action}>{formatAction(action)}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Entity Type</label>
            <select
              name="entityType"
              value={filters.entityType}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Types</option>
              {filterOptions?.entityTypes?.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>User</label>
            <select
              name="userEmail"
              value={filters.userEmail}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Users</option>
              {filterOptions?.users?.map(user => (
                <option key={user} value={user}>{user}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Module</label>
            <select
              name="module"
              value={filters.module}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Modules</option>
              {filterOptions?.modules?.map(mod => (
                <option key={mod} value={mod}>{mod}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group filter-actions">
            <button onClick={clearFilters} className="btn-clear">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="audit-table-container">
        <div className="table-header">
          <span className="results-count">{totalElements.toLocaleString()} logs found</span>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="no-data">No audit logs found</div>
        ) : (
          <table className="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Entity Type</th>
                <th>Entity</th>
                <th>User</th>
                <th>IP Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className={!log.successful ? 'failed-row' : ''}>
                  <td className="timestamp-cell">{formatDate(log.timestamp)}</td>
                  <td>
                    <span className={`action-badge ${getActionBadgeClass(log.action)}`}>
                      {formatAction(log.action)}
                    </span>
                  </td>
                  <td>{log.entityType || '-'}</td>
                  <td className="entity-cell">
                    <span className="entity-name">{log.entityName || log.entityId || '-'}</span>
                  </td>
                  <td className="user-cell">
                    <span className="user-email">{log.userEmail || '-'}</span>
                    {log.userRole && <span className="user-role">{log.userRole}</span>}
                  </td>
                  <td>{log.ipAddress || '-'}</td>
                  <td>
                    <span className={`status-badge ${log.successful ? 'status-success' : 'status-failed'}`}>
                      {log.successful ? 'Success' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => viewLogDetails(log)}
                      className="btn-view"
                      title="View Details"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setPage(0)}
              disabled={page === 0}
              className="pagination-btn"
            >
              First
            </button>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="pagination-btn"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              className="pagination-btn"
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedLog && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content audit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit Log Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-row">
                  <span className="detail-label">Timestamp:</span>
                  <span className="detail-value">{formatDate(selectedLog.timestamp)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Action:</span>
                  <span className={`action-badge ${getActionBadgeClass(selectedLog.action)}`}>
                    {formatAction(selectedLog.action)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Entity Type:</span>
                  <span className="detail-value">{selectedLog.entityType || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Entity ID:</span>
                  <span className="detail-value">{selectedLog.entityId || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Entity Name:</span>
                  <span className="detail-value">{selectedLog.entityName || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User:</span>
                  <span className="detail-value">{selectedLog.userEmail || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">User Role:</span>
                  <span className="detail-value">{selectedLog.userRole || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">IP Address:</span>
                  <span className="detail-value">{selectedLog.ipAddress || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Module:</span>
                  <span className="detail-value">{selectedLog.module || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Request URI:</span>
                  <span className="detail-value">{selectedLog.requestUri || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">HTTP Method:</span>
                  <span className="detail-value">{selectedLog.httpMethod || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`status-badge ${selectedLog.successful ? 'status-success' : 'status-failed'}`}>
                    {selectedLog.successful ? 'Success' : 'Failed'}
                  </span>
                </div>
                {selectedLog.description && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Description:</span>
                    <span className="detail-value">{selectedLog.description}</span>
                  </div>
                )}
                {selectedLog.changedFields && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Changed Fields:</span>
                    <span className="detail-value">{selectedLog.changedFields}</span>
                  </div>
                )}
                {!selectedLog.successful && selectedLog.failureReason && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Failure Reason:</span>
                    <span className="detail-value error-text">{selectedLog.failureReason}</span>
                  </div>
                )}
                {selectedLog.oldValue && (
                  <div className="detail-row full-width">
                    <span className="detail-label">Old Value:</span>
                    <pre className="json-value">{selectedLog.oldValue}</pre>
                  </div>
                )}
                {selectedLog.newValue && (
                  <div className="detail-row full-width">
                    <span className="detail-label">New Value:</span>
                    <pre className="json-value">{selectedLog.newValue}</pre>
                  </div>
                )}
                {selectedLog.userAgent && (
                  <div className="detail-row full-width">
                    <span className="detail-label">User Agent:</span>
                    <span className="detail-value user-agent">{selectedLog.userAgent}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
